import React, { useEffect, useState } from 'react';
import { ArrowRightIcon, DumbbellIcon, FlaskConicalIcon, BrainIcon, ChevronDownIcon, PlayCircleIcon, ImageIcon, UserIcon, CheckIcon, XIcon, ShieldCheckIcon, ClockIcon, UsersIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/Button';
import { AnimatedSection } from '../components/ui/AnimatedSection';
import { SectionHeading } from '../components/ui/SectionHeading';
function VideoEmbed() {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoId = '7qEBTvh3c4M';
  return <div className="relative rounded-2xl overflow-hidden shadow-[0_12px_40px_rgba(0,0,0,0.5)]" data-id="element-336">
      {!isPlaying ? <div className="relative aspect-video cursor-pointer group" onClick={() => setIsPlaying(true)} data-id="element-337">
          {/* YouTube thumbnail */}
          <img src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`} alt="How We Fix Metabolism at the Root" className="w-full h-full object-cover" data-id="element-338" />
          {/* Dark cinematic overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/50 group-hover:via-black/20 transition-all duration-500" data-id="element-339" />
          {/* Play button */}
          <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-340">
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gold/90 group-hover:bg-gold group-hover:scale-110 transition-all duration-300 flex items-center justify-center shadow-[0_0_40px_rgba(212,175,55,0.3)]" data-id="element-341">
              <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8 md:w-10 md:h-10 text-black ml-1" data-id="element-342">
                <path d="M8 5.14v13.72a1 1 0 001.5.86l11.04-6.86a1 1 0 000-1.72L9.5 4.28a1 1 0 00-1.5.86z" fill="currentColor" data-id="element-343" />
              </svg>
            </div>
          </div>
          {/* Bottom text overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8" data-id="element-344">
            <p className="text-gold text-xs md:text-sm font-bold uppercase tracking-[0.2em] mb-1" data-id="element-345">
              Watch Now
            </p>
            <p className="text-white/90 text-sm md:text-lg font-heading font-semibold" data-id="element-346">
              How We Fix Metabolism at the Root
            </p>
          </div>
        </div> : <div className="aspect-video" data-id="element-347">
          <iframe src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`} title="How We Help Metabolically Stuck High Performers Restore Their Metabolism" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" data-id="element-348" />
        </div>}
    </div>;
}
function BMSDiagram() {
  return <div className="relative w-full max-w-2xl mx-auto aspect-square flex items-center justify-center" data-id="element-349">
      {/* Triangle Lines */}
      <svg viewBox="0 0 500 450" className="absolute inset-0 w-full h-full" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-350">
        <line x1="250" y1="60" x2="70" y2="380" stroke="#D4AF37" strokeWidth="1.5" strokeOpacity="0.4" data-id="element-351" />
        <line x1="250" y1="60" x2="430" y2="380" stroke="#D4AF37" strokeWidth="1.5" strokeOpacity="0.4" data-id="element-352" />
        <line x1="70" y1="380" x2="430" y2="380" stroke="#D4AF37" strokeWidth="1.5" strokeOpacity="0.4" data-id="element-353" />
        <line x1="250" y1="60" x2="70" y2="380" stroke="#D4AF37" strokeWidth="0.5" strokeOpacity="0.15" data-id="element-354" />
        <line x1="250" y1="60" x2="430" y2="380" stroke="#D4AF37" strokeWidth="0.5" strokeOpacity="0.15" data-id="element-355" />
        <line x1="70" y1="380" x2="430" y2="380" stroke="#D4AF37" strokeWidth="0.5" strokeOpacity="0.15" data-id="element-356" />
        <circle cx="250" cy="60" r="8" fill="#D4AF37" fillOpacity="0.8" data-id="element-357" />
        <circle cx="250" cy="60" r="16" fill="#D4AF37" fillOpacity="0.1" data-id="element-358" />
        <circle cx="70" cy="380" r="8" fill="#D4AF37" fillOpacity="0.8" data-id="element-359" />
        <circle cx="70" cy="380" r="16" fill="#D4AF37" fillOpacity="0.1" data-id="element-360" />
        <circle cx="430" cy="380" r="8" fill="#22C55E" fillOpacity="0.8" data-id="element-361" />
        <circle cx="430" cy="380" r="16" fill="#22C55E" fillOpacity="0.1" data-id="element-362" />
        <text x="250" y="255" textAnchor="middle" fill="#D4AF37" fontSize="13" fontWeight="600" letterSpacing="0.15em" opacity="0.6" data-id="element-363">
          BIOSHIFT
        </text>
      </svg>

      <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 text-center w-56" data-id="element-364">
        <p className="text-gold font-bold text-lg tracking-wider uppercase font-heading" data-id="element-365">
          Mind
        </p>
        <p className="text-muted text-xs mt-2 leading-relaxed" data-id="element-366">
          Thoughts • Habits • Stress • Identity
        </p>
      </div>

      <div className="absolute bottom-0 left-0 translate-y-2 text-center w-56 md:-translate-x-4" data-id="element-367">
        <p className="text-gold font-bold text-lg tracking-wider uppercase font-heading" data-id="element-368">
          Body
        </p>
        <p className="text-muted text-xs mt-2 leading-relaxed" data-id="element-369">
          Metabolism • Hormones • Nutrition • Strength
        </p>
      </div>

      <div className="absolute bottom-0 right-0 translate-y-2 text-center w-56 md:translate-x-4" data-id="element-370">
        <p className="text-[#22C55E] font-bold text-lg tracking-wider uppercase font-heading" data-id="element-371">
          Alignment
        </p>
        <p className="text-muted text-xs mt-2 leading-relaxed" data-id="element-372">
          Purpose • Boundaries • Peace • Lifestyle
        </p>
      </div>
    </div>;
}
export function Home() {
  const [showFloatingBar, setShowFloatingBar] = useState(false);
  const [currentTestimonialSlide, setCurrentTestimonialSlide] = useState(0);
  const [currentTransformationSlide, setCurrentTransformationSlide] = useState(0);
  // Testimonial data
  const testimonials = [{
    name: 'Liz P., CEO',
    result: 'Lost 25 lbs · Reversed PCOS & Fatty Liver',
    quote: 'I felt stuck in my body no matter what I tried. BioShift gave me structure, clarity, and a system that actually worked.\n\nNow I wake up with energy again — and I feel in control of my health for the first time in years.'
  }, {
    name: 'Dr. Michelle R., CEO & Founder',
    result: 'Reversed Insulin Resistance · Lost 20 lbs · +5% Muscle',
    quote: "I had tried everything before, but nothing addressed the root problem. BioShift didn't just give me a plan — they helped me fix my metabolism from the inside out.\n\nThis completely changed how my body responds to food, training, and stress."
  }, {
    name: 'Scott S., Entrepreneur',
    result: 'Gained 3 lbs Muscle · Lost 18 lbs',
    quote: "I was successful in business, but my health was falling apart. The combination of strength training and mindset work changed everything for me.\n\nI'm stronger, more focused, and showing up better in every area of my life."
  }, {
    name: 'Debbie',
    result: 'Improved Energy & Metabolism · Lost 50 lbs',
    quote: 'Desde que empecé con BioShift, no solo he fortalecido mi cuerpo, sino también mi mente. Cada sesión me llena de confianza, propósito y comunidad. Aprendí a valorarme más allá de un número y a sanar desde adentro hacia afuera. Sé que Dios abrió esta puerta para mi transformación.'
  }, {
    name: 'Ludivina, 38',
    result: 'Lost 24 lbs · Gained Muscle',
    quote: "I struggled with weight for years and couldn't keep it off. BioShift helped me understand my body and build habits that actually stick.\n\nNow I feel confident again."
  }];
  // Transformation data
  const transformations = [{
    src: 'https://cdn.magicpatterns.com/uploads/cjv87Ds8X1Q8YDzg3royoR/Untitled_design.png-2.png',
    alt: 'Liz P. transformation'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/7Nt2CXeYiTw11hXCDjAwpi/Untitled_design.png.png',
    alt: 'Dr. Michelle R. transformation'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/aAP1RvSw4nfjsHXRHhsNLZ/IMG_1676.jpg',
    alt: 'Scott S. transformation'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/eGgtfuem1rDSxP9gusGZNa/NEW_PIC.jpg',
    alt: 'Debbie transformation'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/qVTxygFUpLg3ihnm1siFeG/Untitled_design_2.png.png',
    alt: 'Ludivina transformation'
  }];
  // Determine items per view based on screen width (approximate for initial render)
  const [itemsPerView, setItemsPerView] = useState(3);
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setItemsPerView(1);
      } else if (window.innerWidth < 1024) {
        setItemsPerView(2);
      } else {
        setItemsPerView(3);
      }
    };
    // Initial check
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const nextTestimonial = () => {
    setCurrentTestimonialSlide(prev => prev === testimonials.length - itemsPerView ? 0 : prev + 1);
  };
  const prevTestimonial = () => {
    setCurrentTestimonialSlide(prev => prev === 0 ? testimonials.length - itemsPerView : prev - 1);
  };
  const nextTransformation = () => {
    setCurrentTransformationSlide(prev => prev === transformations.length - itemsPerView ? 0 : prev + 1);
  };
  const prevTransformation = () => {
    setCurrentTransformationSlide(prev => prev === 0 ? transformations.length - itemsPerView : prev - 1);
  };
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 800) {
        setShowFloatingBar(true);
      } else {
        setShowFloatingBar(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <main className="w-full relative" data-id="element-373">
      {/* ═══════════════════════════════════════════════════════════════
                                                                    SECTION 1 — HERO
                                                                ═══════════════════════════════════════════════════════════════ */}
      <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden pt-32 pb-20" data-id="element-374">
        <div className="absolute inset-0 z-0" data-id="element-375">
          <img src="https://cdn.magicpatterns.com/uploads/3zjPm1UVbwJm3AzcLKKeEq/99afff25-f329-4896-8be3-51e26cd0a74b.jpg" alt="BioShift team consultation" className="w-full h-full object-cover object-center" data-id="element-376" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-dark" data-id="element-377"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center flex flex-col items-center w-full" data-id="element-378">
          <AnimatedSection delay={0.1} data-id="element-379">
            <span className="text-gold text-sm md:text-base font-medium uppercase tracking-[0.3em] mb-6 block" data-id="element-380">
              Human Performance & Longevity
            </span>
          </AnimatedSection>

          <AnimatedSection delay={0.2} data-id="element-381">
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-8 leading-[1.1] max-w-5xl text-balance" data-id="element-382">
              Restore Your Metabolism. <br className="hidden md:block" data-id="element-383" />
              <span className="text-white/90" data-id="element-384">Rebuild Your Strength.</span>{' '}
              <br className="hidden md:block" data-id="element-385" />
              <span className="text-gold" data-id="element-386">Reclaim Your Life.</span>
            </h1>
          </AnimatedSection>

          <AnimatedSection delay={0.3} data-id="element-387">
            <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-12 leading-relaxed" data-id="element-388">
              BioShift helps high-performing professionals restore metabolic
              function, rebuild their body, and regain their energy through a
              system that addresses the root causes of health issues — not just
              the symptoms.
            </p>
          </AnimatedSection>

          <AnimatedSection delay={0.4} className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full sm:w-auto mb-16" data-id="element-389">
            <Button href="https://holistic-health.bioshift.net/calender-booking" size="lg" className="w-full sm:w-auto" data-id="element-390">
              Apply for Coaching
            </Button>
            <Button href="#methodology" variant="secondary" size="lg" className="w-full sm:w-auto" data-id="element-391">
              Discover the BioShift Method
            </Button>
          </AnimatedSection>

          {/* YouTube Video */}
          <AnimatedSection delay={0.5} className="w-full max-w-4xl mx-auto" data-id="element-392">
            <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-white font-heading text-center leading-tight mb-3" data-id="element-393">
              How We Help Metabolically Stuck High Performers Restore Their
              Metabolism in 90 Days
            </h3>
            <p className="text-muted text-center text-sm md:text-base max-w-2xl mx-auto mb-8" data-id="element-394">
              Through our BMS Method — a structured system designed to restore
              metabolic function and long-term health in 3–6 months
            </p>
            <VideoEmbed data-id="element-395" />
          </AnimatedSection>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 animate-bounce hidden md:block" data-id="element-396">
          <ChevronDownIcon className="w-8 h-8 text-gold/50" data-id="element-397" />
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
                                                                    SOCIAL PROOF BAR
                                                                ═══════════════════════════════════════════════════════════════ */}
      <AnimatedSection delay={0.2} data-id="element-398">
        <div className="w-full bg-surface/50 backdrop-blur border-y border-border py-6" data-id="element-399">
          <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-400">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center" data-id="element-401">
              <div data-id="element-402">
                <p className="text-gold text-3xl md:text-4xl font-bold font-heading mb-1" data-id="element-403">
                  500+
                </p>
                <p className="text-muted text-xs uppercase tracking-wider" data-id="element-404">
                  Clients Transformed
                </p>
              </div>
              <div data-id="element-405">
                <p className="text-gold text-3xl md:text-4xl font-bold font-heading mb-1" data-id="element-406">
                  12,000+
                </p>
                <p className="text-muted text-xs uppercase tracking-wider" data-id="element-407">
                  Pounds of Fat Lost
                </p>
              </div>
              <div data-id="element-408">
                <p className="text-gold text-3xl md:text-4xl font-bold font-heading mb-1" data-id="element-409">
                  97%
                </p>
                <p className="text-muted text-xs uppercase tracking-wider" data-id="element-410">
                  Client Retention Rate
                </p>
              </div>
              <div data-id="element-411">
                <p className="text-gold text-3xl md:text-4xl font-bold font-heading mb-1" data-id="element-412">
                  4.9/5
                </p>
                <p className="text-muted text-xs uppercase tracking-wider" data-id="element-413">
                  Average Client Rating
                </p>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* ═══════════════════════════════════════════════════════════════
                                                                    SECTION 2 — WHY HEALTHCARE FAILED US
                                                                ═══════════════════════════════════════════════════════════════ */}
      <section className="py-24 md:py-32 bg-surface" data-id="element-414">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-415">
          <AnimatedSection data-id="element-416">
            <SectionHeading overline="The Real Problem" title="Why Has the Health Care System Failed You?" data-id="element-417" />
          </AnimatedSection>

          <AnimatedSection delay={0.2} data-id="element-418">
            <div className="max-w-3xl mx-auto mt-12 text-lg text-muted leading-relaxed space-y-6" data-id="element-419">
              <p data-id="element-420">
                For decades, modern healthcare has focused on treating symptoms
                rather than solving the underlying causes of disease.
              </p>

              <div data-id="element-421">
                <p className="mb-4" data-id="element-422">People are prescribed medications for:</p>
                <ul className="space-y-3 ml-2" data-id="element-423">
                  {['high blood pressure', 'diabetes', 'fatigue', 'hormonal imbalances', 'weight gain'].map((item, idx) => <li key={idx} className="flex items-center text-gray-300" data-id="element-424">
                      <div className="w-1.5 h-1.5 bg-gold rounded-full mr-4 flex-shrink-0" data-id="element-425"></div>
                      {item}
                    </li>)}
                </ul>
              </div>

              <p data-id="element-426">
                But the root causes of these conditions are rarely addressed.
              </p>

              <p className="text-white/80" data-id="element-427">
                Metabolic dysfunction. Chronic stress. Poor nutrition. Muscle
                loss. Lifestyle habits.
              </p>

              <p data-id="element-428">These are the real drivers behind what you're experiencing.</p>

              <p className="text-white/50 text-2xl font-heading text-center py-4" data-id="element-429">
                And yet…
              </p>

              <p data-id="element-430">
                Most solutions only attempt to manage the outcome — not fix the
                system that created it.
              </p>

              <div className="pt-8 mt-4 border-t border-border space-y-4" data-id="element-431">
                <p className="text-white font-bold text-xl" data-id="element-432">
                  That's why results don't last.
                </p>
                <p className="text-muted" data-id="element-433">
                  Because nothing truly changed at the root.
                </p>
                <p className="text-gold font-semibold text-xl mt-6" data-id="element-434">
                  At BioShift, we do things differently.
                </p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
                                                                    HOW YOU WORK WITH US
                                                                ═══════════════════════════════════════════════════════════════ */}
      <section className="py-24 md:py-32 bg-dark" data-id="element-435">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-436">
          <AnimatedSection data-id="element-437">
            <SectionHeading overline="How You Work With Us" title="Your Transformation Process" description="A structured, step-by-step system designed to restore your metabolism, rebuild strength, and align your health from the inside out." data-id="element-438" />
          </AnimatedSection>

          <div className="max-w-4xl mx-auto mt-16 space-y-0" data-id="element-439">
            {[{
            step: '01',
            title: 'Advanced Testing & Health Assessment',
            desc: 'We begin with comprehensive blood work and in-depth health analysis to uncover hidden metabolic dysfunction, hormonal imbalances, and inflammation patterns. This establishes your true baseline — not just "normal ranges."'
          }, {
            step: '02',
            title: 'Clinical Results Interpretation',
            desc: 'Our team analyzes your biomarkers beyond standard medicine, identifying root causes — not just symptoms — and translating your data into a clear, personalized health strategy.'
          }, {
            step: '03',
            title: 'Personalized Strategy Design',
            desc: 'Based on your results, we design a fully customized plan including strength training, functional nutrition, and lifestyle protocols tailored to your physiology and goals.'
          }, {
            step: '04',
            title: 'Performance Training & Nutrition',
            desc: "You'll follow a structured strength and nutrition protocol designed to restore metabolic function, improve insulin sensitivity, and rebuild lean muscle efficiently."
          }, {
            step: '05',
            title: 'Implementation & Accountability',
            desc: "Through our system and coaching, you'll execute daily habits with guidance, tracking, and continuous adjustments to ensure real, measurable progress."
          }, {
            step: '06',
            title: 'Medical Oversight & Optimization',
            desc: 'Our medical team supports your journey by monitoring your progress, adjusting protocols, and ensuring your internal health markers improve alongside your physical results.'
          }, {
            step: '07',
            title: 'Mindset & Identity Transformation',
            desc: 'We address the root — your mindset and identity. True transformation happens when your beliefs align with your actions, allowing results to become sustainable, not temporary.'
          }].map((item, i) => <AnimatedSection key={`step-${i}`} delay={i * 0.08} data-id="element-440">
                <div className="flex gap-6 md:gap-8 py-8 border-b border-border/50 last:border-b-0 group" data-id="element-441">
                  <div className="flex-shrink-0 w-14 h-14 rounded-full border-2 border-gold/30 group-hover:border-gold transition-colors duration-300 flex items-center justify-center" data-id="element-442">
                    <span className="text-gold font-bold text-sm font-heading" data-id="element-443">
                      {item.step}
                    </span>
                  </div>
                  <div className="flex-1 pt-1" data-id="element-444">
                    <h3 className="text-xl md:text-2xl font-bold text-white font-heading mb-3" data-id="element-445">
                      {item.title}
                    </h3>
                    <p className="text-muted leading-relaxed text-base md:text-lg" data-id="element-446">
                      {item.desc}
                    </p>
                  </div>
                </div>
              </AnimatedSection>)}
          </div>

          <AnimatedSection delay={0.6} data-id="element-447">
            <div className="max-w-3xl mx-auto mt-16 text-center space-y-6" data-id="element-448">
              <p className="text-white font-bold text-2xl md:text-3xl font-heading" data-id="element-449">
                This is not a program. It's a{' '}
                <span className="text-gold" data-id="element-450">restoration process.</span>
              </p>
              <p className="text-muted text-lg leading-relaxed" data-id="element-451">
                If you're ready to stop guessing and start addressing the root
                cause of your health…
              </p>
              <div className="pt-4" data-id="element-452">
                <Button href="https://holistic-health.bioshift.net/calender-booking" size="lg" data-id="element-453">
                  Apply Now — Limited Spots
                </Button>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
                                                                    SECTION 3 — THE BIOSHIFT METHOD
                                                                ═══════════════════════════════════════════════════════════════ */}
      <section id="methodology" className="py-24 md:py-32 relative overflow-hidden" style={{
      backgroundColor: '#1A4A3A'
    }} data-id="element-454">
        <div className="absolute inset-0 z-0" data-id="element-455">
          <img src="https://cdn.magicpatterns.com/uploads/9f6L1XyM8E51Hm5nAAdz6X/Mind,_body,_spirit_alignment.png" alt="Mind, Body, Spirit alignment" className="w-full h-full object-cover object-center opacity-40" data-id="element-456" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50" data-id="element-457"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10" data-id="element-458">
          <AnimatedSection data-id="element-459">
            <SectionHeading overline="The Framework" title="THE BIOSHIFT BMS METHOD™" data-id="element-460" />
            <p className="text-center text-muted text-lg max-w-3xl mx-auto mt-4 leading-relaxed" data-id="element-461">
              The BioShift BMS Method™ is a structured system designed to
              create lasting transformation by addressing the{' '}
              <span className="text-gold font-semibold" data-id="element-462">
                Body, Mind, and Spirit
              </span>{' '}
              — not just surface-level symptoms.
            </p>
          </AnimatedSection>

          <AnimatedSection delay={0.2} data-id="element-463">
            <div className="max-w-3xl mx-auto mt-16 text-lg text-muted leading-relaxed space-y-6 text-center" data-id="element-464">
              <p className="text-white font-semibold text-2xl font-heading py-2" data-id="element-465">
                The human being must be approached as a whole.
              </p>

              <p className="text-gold text-xl tracking-wide font-semibold" data-id="element-466">
                Body. Mind. Spirit.
              </p>

              <p data-id="element-467">
                Most programs focus on isolated solutions — workouts, diets, or
                temporary habits.
              </p>

              <p data-id="element-468">
                But real, lasting transformation happens when the entire system
                is restored from the inside out.
              </p>

              <p className="text-white font-semibold text-xl" data-id="element-469">
                That's what makes BioShift different.
              </p>

              <p data-id="element-470">
                We don't chase symptoms. We identify and correct the root cause
                of dysfunction — physically, mentally, and spiritually.
              </p>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.3} data-id="element-471">
            <div className="max-w-3xl mx-auto mt-12 text-center" data-id="element-472">
              <p className="text-white font-bold text-xl mb-8 font-heading" data-id="element-473">
                What the BMS Method™ is designed to do:
              </p>
              <ul className="space-y-4 text-left max-w-xl mx-auto" data-id="element-474">
                {['Restore the body by improving metabolic health, strength, and energy', 'Renew the mind by addressing habits, beliefs, and identity', 'Strengthen the spirit to create alignment, discipline, and purpose', 'Eliminate root causes instead of masking symptoms', 'Build a structured path that produces measurable, lasting results'].map((item, idx) => <li key={idx} className="flex items-start text-gray-300 text-lg" data-id="element-475">
                    <CheckIcon className="w-5 h-5 text-gold mr-4 flex-shrink-0 mt-1" data-id="element-476" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
                                                                    SECTION 5 — WHY THIS WORKS
                                                                ═══════════════════════════════════════════════════════════════ */}
      <section className="py-24 md:py-32 bg-dark" data-id="element-477">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-478">
          <AnimatedSection data-id="element-479">
            <SectionHeading overline="The Difference" title="Why This Works When Everything Else Hasn't" data-id="element-480" />
          </AnimatedSection>

          <div className="max-w-3xl mx-auto mt-16 space-y-8" data-id="element-481">
            {["Because we don't guess — we test and assess", "Because we don't rely on motivation — we build structure", "Because we don't treat symptoms — we fix root causes", "Because we don't separate the body from the mind"].map((item, i) => <AnimatedSection key={i} delay={i * 0.1 + 0.2} data-id="element-482">
                <div className="flex items-start group" data-id="element-483">
                  <div className="w-10 h-10 rounded-sm bg-gold/10 border border-gold/30 flex items-center justify-center mr-6 flex-shrink-0 mt-0.5 group-hover:bg-gold/20 transition-colors" data-id="element-484">
                    <CheckIcon className="w-5 h-5 text-gold" data-id="element-485" />
                  </div>
                  <p className="text-white text-xl md:text-2xl font-medium leading-snug" data-id="element-486">
                    {item}
                  </p>
                </div>
              </AnimatedSection>)}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
                                                                    WHO THIS IS FOR (NEW)
                                                                ═══════════════════════════════════════════════════════════════ */}
      <section className="py-24 md:py-32 bg-surface" data-id="element-487">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-488">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-489">
            {/* Left Column */}
            <AnimatedSection direction="right" data-id="element-490">
              <span className="text-gold text-sm font-medium uppercase tracking-[0.3em] mb-4 block" data-id="element-491">
                IS THIS YOU?
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-10 font-heading" data-id="element-492">
                BioShift Is Built For High-Performers
              </h2>
              <ul className="space-y-6" data-id="element-493">
                {["You earn $150K+ and your health hasn't kept pace with your success", "You've tried diets, trainers, and programs that didn't last", "You're tired of guessing and want a data-driven approach", 'You want accountability, not just another app or PDF', "You're ready to invest in yourself at the highest level", 'You believe health is the foundation of everything else'].map((item, i) => <li key={i} className="flex items-start" data-id="element-494">
                    <CheckIcon className="w-5 h-5 text-gold mr-4 flex-shrink-0 mt-0.5" data-id="element-495" />
                    <span className="text-white text-base leading-relaxed" data-id="element-496">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </AnimatedSection>

            {/* Right Column */}
            <AnimatedSection direction="left" data-id="element-497">
              <div className="bg-dark border border-border p-10 h-full" data-id="element-498">
                <h3 className="text-2xl font-bold text-white/60 mb-8 font-heading" data-id="element-499">
                  This Program Is Not For Everyone
                </h3>
                <ul className="space-y-6" data-id="element-500">
                  {["You're looking for a quick fix or magic pill", "You're not willing to commit to 6 months of structured work", 'You want someone to do it for you without personal effort', "You're not ready to invest in premium, high-touch coaching"].map((item, i) => <li key={i} className="flex items-start" data-id="element-501">
                      <XIcon className="w-5 h-5 text-white/30 mr-4 flex-shrink-0 mt-0.5" data-id="element-502" />
                      <span className="text-muted text-sm leading-relaxed" data-id="element-503">
                        {item}
                      </span>
                    </li>)}
                </ul>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
                                                                    SECTION 6 — THE BIOSHIFT PILLARS
                                                                ═══════════════════════════════════════════════════════════════ */}
      <section className="py-24 md:py-32 bg-dark" data-id="element-504">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-505">
          <AnimatedSection data-id="element-506">
            <SectionHeading overline="The Foundation" title="The BioShift Pillars" description="A comprehensive three-pillar system for identifying and fixing the root causes of health issues rather than simply treating symptoms." data-id="element-507" />
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16" data-id="element-508">
            {[{
            icon: DumbbellIcon,
            title: 'Strength Training',
            image: 'https://cdn.magicpatterns.com/uploads/jXpwBPuQqhP28LXBWDjX3C/67A4979B-9D7F-4061-A914-5A777CED4F9B_Original.jpg',
            desc1: 'Build muscle, restore metabolic health, and create a resilient body capable of sustaining long-term health and performance.',
            desc2: 'Strength training improves insulin sensitivity, hormonal balance, bone density, and physical capacity.'
          }, {
            icon: FlaskConicalIcon,
            title: 'Functional Nutrition',
            image: 'https://cdn.magicpatterns.com/uploads/qPMYhy8GtxUKEEoW75Wg8t/istockphoto-1960974311-612x612.jpg',
            desc1: 'Use targeted nutrition strategies and biomarker insights to restore metabolic balance, regulate blood sugar, reduce inflammation, and optimize energy production.',
            desc2: 'Nutrition protocols are designed to support both fat loss and long-term health.'
          }, {
            icon: BrainIcon,
            title: 'Mindset Recalibration',
            image: 'https://cdn.magicpatterns.com/uploads/mJsTn849cyDjXc88jReknN/istockphoto-2225735012-612x612.jpg',
            desc1: 'Long-term transformation requires internal alignment.',
            desc2: 'Through mindset coaching and identity work, BioShift helps individuals build discipline, clarity, and purpose so healthy behaviors become sustainable.'
          }].map((pillar, i) => <AnimatedSection key={i} delay={i * 0.1 + 0.2} data-id="element-509">
                <div className="bg-surface border border-border hover:border-gold/30 transition-colors h-full flex flex-col overflow-hidden group" data-id="element-510">
                  <div className="aspect-[4/3] bg-dark flex flex-col items-center justify-center border-b border-border relative overflow-hidden" data-id="element-511">
                    {pillar.image ? <img src={pillar.image} alt={pillar.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-512" /> : <>
                        <ImageIcon className="w-12 h-12 text-gold/20 mb-3 group-hover:scale-110 transition-transform duration-500" data-id="element-513" />
                        <span className="text-muted text-xs uppercase tracking-wider" data-id="element-514">
                          Replace with pillar image
                        </span>
                      </>}
                  </div>
                  <div className="w-full h-1 bg-gold scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-500" data-id="element-515"></div>
                  <div className="p-8 flex-1 flex flex-col" data-id="element-516">
                    <pillar.icon className="w-10 h-10 text-gold mb-6" data-id="element-517" />
                    <h3 className="text-2xl font-bold text-white mb-4 font-heading" data-id="element-518">
                      {pillar.title}
                    </h3>
                    <div className="space-y-4 text-muted leading-relaxed flex-1" data-id="element-519">
                      <p data-id="element-520">{pillar.desc1}</p>
                      <p data-id="element-521">{pillar.desc2}</p>
                    </div>
                  </div>
                </div>
              </AnimatedSection>)}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
                                                                    SECTION 7 — CLIENT TRANSFORMATIONS
                                                                ═══════════════════════════════════════════════════════════════ */}
      <section id="testimonials" className="py-24 md:py-32 bg-surface" data-id="element-522">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-523">
          <AnimatedSection data-id="element-524">
            <SectionHeading overline="Results" title="Real People. Real Transformations." description="These are not temporary results — these are lives that have been rebuilt from the inside out." data-id="element-525" />
          </AnimatedSection>

          {/* Testimonial Carousel */}
          <AnimatedSection delay={0.2} data-id="element-526">
            <div className="mt-16 relative" data-id="element-527">
              <div className="overflow-hidden px-2 pb-8" data-id="element-528">
                <div className="flex transition-transform duration-500 ease-in-out" style={{
                transform: `translateX(-${currentTestimonialSlide * (100 / itemsPerView)}%)`
              }} data-id="element-529">
                  {testimonials.map((item, i) => <div key={i} className="flex-shrink-0 px-4" style={{
                  width: `${100 / itemsPerView}%`
                }} data-id="element-530">
                      <div className="bg-dark border border-border p-8 h-full flex flex-col rounded-lg" data-id="element-531">
                        <div className="flex items-center gap-4 mb-6" data-id="element-532">
                          <div className="w-10 h-10 rounded-full bg-gold/10 border border-gold/20 flex items-center justify-center flex-shrink-0" data-id="element-533">
                            <span className="text-gold font-bold text-sm font-heading" data-id="element-534">
                              {item.name.charAt(0)}
                            </span>
                          </div>
                          <div data-id="element-535">
                            <p className="text-white font-bold font-heading text-base leading-tight" data-id="element-536">
                              {item.name}
                            </p>
                            <p className="text-gold text-xs font-medium mt-0.5" data-id="element-537">
                              {item.result}
                            </p>
                          </div>
                        </div>
                        <div className="text-muted italic leading-relaxed flex-1 text-[15px] space-y-3" data-id="element-538">
                          {item.quote.split('\n\n').map((paragraph: string, pi: number) => <p key={pi} data-id="element-539">
                                {pi === 0 ? '"' : ''}
                                {paragraph}
                                {pi === item.quote.split('\n\n').length - 1 ? '"' : ''}
                              </p>)}
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>

              {/* Navigation Controls */}
              <div className="flex justify-center items-center gap-6 mt-4" data-id="element-540">
                <button onClick={prevTestimonial} className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-white hover:text-gold hover:border-gold transition-colors" aria-label="Previous testimonial" data-id="element-541">
                  <ChevronLeftIcon className="w-5 h-5" data-id="element-542" />
                </button>

                <div className="flex gap-2" data-id="element-543">
                  {Array.from({
                  length: testimonials.length - itemsPerView + 1
                }).map((_, idx) => <button key={idx} onClick={() => setCurrentTestimonialSlide(idx)} className={`w-2.5 h-2.5 rounded-full transition-colors ${currentTestimonialSlide === idx ? 'bg-gold' : 'bg-border hover:bg-muted'}`} aria-label={`Go to slide ${idx + 1}`} data-id="element-544" />)}
                </div>

                <button onClick={nextTestimonial} className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-white hover:text-gold hover:border-gold transition-colors" aria-label="Next testimonial" data-id="element-545">
                  <ChevronRightIcon className="w-5 h-5" data-id="element-546" />
                </button>
              </div>
            </div>
          </AnimatedSection>

          {/* Before & After Transformations */}
          <AnimatedSection delay={0.2} data-id="element-547">
            <div className="mt-20 mb-4" data-id="element-548">
              <h3 className="text-2xl font-bold text-white font-heading text-center mb-2" data-id="element-549">
                Client Transformations
              </h3>
              <p className="text-muted text-sm text-center uppercase tracking-wider" data-id="element-550">
                Real results from real BioShift clients
              </p>
            </div>
          </AnimatedSection>

          {/* Transformations Carousel */}
          <AnimatedSection delay={0.3} data-id="element-551">
            <div className="mt-10 relative" data-id="element-552">
              <div className="overflow-hidden px-2 pb-8" data-id="element-553">
                <div className="flex transition-transform duration-500 ease-in-out" style={{
                transform: `translateX(-${currentTransformationSlide * (100 / itemsPerView)}%)`
              }} data-id="element-554">
                  {transformations.map((img, i) => <div key={i} className="flex-shrink-0 px-3" style={{
                  width: `${100 / itemsPerView}%`
                }} data-id="element-555">
                      <div className="relative rounded-lg overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.4)] group h-full" data-id="element-556">
                        <img src={img.src} alt={img.alt} className="w-full aspect-[4/5] object-cover object-top" data-id="element-557" />
                        {/* Before / After overlay labels */}
                        <div className="absolute bottom-0 left-0 right-0 flex pointer-events-none" data-id="element-558">
                          <div className="flex-1 flex justify-center pb-4" data-id="element-559">
                            <span className="bg-black/70 backdrop-blur-sm text-white text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-1.5 rounded-full" data-id="element-560">
                              Before
                            </span>
                          </div>
                          <div className="flex-1 flex justify-center pb-4" data-id="element-561">
                            <span className="bg-gold/90 backdrop-blur-sm text-black text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-1.5 rounded-full" data-id="element-562">
                              After
                            </span>
                          </div>
                        </div>
                        {/* Subtle gradient at bottom for label readability */}
                        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" data-id="element-563"></div>
                      </div>
                    </div>)}
                </div>
              </div>

              {/* Navigation Controls */}
              <div className="flex justify-center items-center gap-6 mt-2" data-id="element-564">
                <button onClick={prevTransformation} className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-white hover:text-gold hover:border-gold transition-colors" aria-label="Previous transformation" data-id="element-565">
                  <ChevronLeftIcon className="w-5 h-5" data-id="element-566" />
                </button>

                <div className="flex gap-2" data-id="element-567">
                  {Array.from({
                  length: transformations.length - itemsPerView + 1
                }).map((_, idx) => <button key={idx} onClick={() => setCurrentTransformationSlide(idx)} className={`w-2.5 h-2.5 rounded-full transition-colors ${currentTransformationSlide === idx ? 'bg-gold' : 'bg-border hover:bg-muted'}`} aria-label={`Go to slide ${idx + 1}`} data-id="element-568" />)}
                </div>

                <button onClick={nextTransformation} className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-white hover:text-gold hover:border-gold transition-colors" aria-label="Next transformation" data-id="element-569">
                  <ChevronRightIcon className="w-5 h-5" data-id="element-570" />
                </button>
              </div>
            </div>
          </AnimatedSection>

          {/* Video Testimonials */}
          <AnimatedSection delay={0.2} data-id="element-571">
            <div className="mt-20 mb-4" data-id="element-572">
              <h3 className="text-2xl font-bold text-white font-heading text-center mb-2" data-id="element-573">
                Video Testimonials
              </h3>
              <p className="text-muted text-sm text-center uppercase tracking-wider" data-id="element-574">
                Hear directly from our clients
              </p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10 max-w-5xl mx-auto" data-id="element-575">
            {[{
            videoId: 'V0dlIjrE1os',
            title: 'Client Testimonial'
          }, {
            videoId: 'DRcj4xsQ1Uw',
            title: 'Client Testimonial'
          }, {
            videoId: 'z9vCVG4DoO4',
            title: 'Client Testimonial'
          }, {
            videoId: 'C55CX0qUnIA',
            title: 'Client Testimonial'
          }].map((vid, i) => <AnimatedSection key={`vid-${i}`} delay={i * 0.1 + 0.3} data-id="element-576">
                {vid.videoId ? <div className="relative rounded-lg overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.4)] aspect-video" data-id="element-577">
                    <iframe src={`https://www.youtube.com/embed/${vid.videoId}?rel=0&modestbranding=1`} title={vid.title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" data-id="element-578" />
                  </div> : <div className="relative rounded-lg overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.4)] aspect-video bg-dark border border-border flex flex-col items-center justify-center" data-id="element-579">
                    <PlayCircleIcon className="w-12 h-12 text-gold/20 mb-3" data-id="element-580" />
                    <span className="text-muted text-xs uppercase tracking-wider" data-id="element-581">
                      {vid.title}
                    </span>
                  </div>}
              </AnimatedSection>)}
          </div>

          <div className="mt-16 text-center" data-id="element-582">
            <Button href="https://holistic-health.bioshift.net/calender-booking" size="lg" data-id="element-583">
              Book a Free Health Strategy Call
            </Button>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
                                                                    SECTION 8 — THE TEAM
                                                                ═══════════════════════════════════════════════════════════════ */}
      <section className="py-24 md:py-32 bg-dark" data-id="element-584">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-585">
          <AnimatedSection data-id="element-586">
            <SectionHeading overline="Our Team" title="Meet the Team Behind BioShift" data-id="element-587" />
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-16" data-id="element-588">
            {[{
            name: 'Elisa Chiaradia',
            role: 'Founder & Strength Coach',
            image: 'https://cdn.magicpatterns.com/uploads/pGYu2ED4rCuND2v7YZPWJb/65c64ac3-999a-4dd4-a115-3e232fb08b1a_2.jpg',
            bio: 'Driven by personal loss and a relentless pursuit of truth, Elisa founded BioShift to help others reclaim their health through science and faith.'
          }, {
            name: 'Diego Caceres',
            role: 'Nutritionist',
            image: 'https://cdn.magicpatterns.com/uploads/nPDPQiZmaTvYzrjPFt7CUR/IMG_0741.jpg',
            bio: 'Specializing in metabolic nutrition and biomarker analysis to create personalized protocols that restore balance and optimize performance.'
          }, {
            name: 'Milton Alvarez',
            role: 'Human Performance Coach',
            image: 'https://cdn.magicpatterns.com/uploads/8wirbT2BX9uviDdJMuWp7K/IMG_0862.jpg',
            bio: 'Helping clients rebuild identity, discipline, and purpose through structured mindset recalibration and accountability.'
          }, {
            name: 'Kelsey Walker MD',
            role: 'Medical Advisor',
            image: 'https://cdn.magicpatterns.com/uploads/iS6NwQq3E47EAwiJnEVVU3/668f552e-0a0e-429c-8af3-2702d87b9d03.jpg',
            bio: 'Providing clinical oversight and ensuring all protocols are grounded in the latest metabolic and longevity research.'
          }].map((member, i) => <AnimatedSection key={i} delay={i * 0.1} data-id="element-589">
                <div className="bg-surface border border-border p-6 h-full flex flex-col group hover:border-gold/30 transition-colors" data-id="element-590">
                  <div className="aspect-[3/4] bg-dark border border-border flex flex-col items-center justify-center mb-6 overflow-hidden relative" data-id="element-591">
                    {member.image ? <img src={member.image} alt={member.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" data-id="element-592" /> : <>
                        <UserIcon className="w-12 h-12 text-gold/20 mb-3 group-hover:scale-110 transition-transform duration-500" data-id="element-593" />
                        <span className="text-muted text-[10px] uppercase tracking-wider text-center px-2" data-id="element-594">
                          Replace with team photo
                        </span>
                      </>}
                  </div>
                  <h3 className="text-white font-bold text-xl font-heading mb-1" data-id="element-595">
                    {member.name}
                  </h3>
                  <p className="text-gold text-sm uppercase tracking-wider mb-4 font-medium" data-id="element-596">
                    {member.role}
                  </p>
                  <p className="text-muted text-sm leading-relaxed flex-1" data-id="element-597">
                    {member.bio}
                  </p>
                </div>
              </AnimatedSection>)}
          </div>

          <AnimatedSection delay={0.5} data-id="element-598">
            <p className="text-muted text-sm text-center italic mt-12" data-id="element-599">
              Team section is expandable — add more members as your team grows.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
                                                                    SECTION 9 — PROGRAMS OVERVIEW
                                                                ═══════════════════════════════════════════════════════════════ */}
      <section className="py-24 md:py-32 bg-surface" data-id="element-600">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-601">
          <AnimatedSection data-id="element-602">
            <SectionHeading overline="Programs" title="How You Can Work With BioShift" data-id="element-603" />
          </AnimatedSection>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-16 max-w-5xl mx-auto" data-id="element-604">
            {[{
            title: 'BioShift Longevity Program',
            image: 'https://cdn.magicpatterns.com/uploads/1gXbmgUo6REB8RMHDJboMy/IMG_0743.jpg',
            desc: '6-month metabolic transformation designed to restore health, optimize performance, and extend healthspan.',
            link: '/programs/longevity'
          }, {
            title: 'Corporate Wellness',
            subtitle: 'CORE PERFORMANCE GROUP',
            image: 'https://cdn.magicpatterns.com/uploads/uAMxn9VdmAfaDZkcuTvjXU/IMG_0744.webp',
            desc: 'Transform your company culture by investing in the metabolic health and physical resilience of your team.',
            link: '/programs/corporate-wellness'
          }].map((program, i) => <AnimatedSection key={i} delay={i * 0.1 + 0.2} data-id="element-605">
                <div className="bg-dark border border-border hover:border-gold/30 transition-colors h-full flex flex-col overflow-hidden group" data-id="element-606">
                  <div className="aspect-[16/10] bg-surface border-b border-border flex flex-col items-center justify-center relative overflow-hidden" data-id="element-607">
                    {program.image ? <img src={program.image} alt={program.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-608" /> : <>
                        <ImageIcon className="w-12 h-12 text-gold/20 mb-3 group-hover:scale-110 transition-transform duration-500" data-id="element-609" />
                        <span className="text-muted text-xs uppercase tracking-wider" data-id="element-610">
                          Replace with program image
                        </span>
                      </>}
                  </div>
                  <div className="p-8 flex-1 flex flex-col" data-id="element-611">
                    <h3 className="text-2xl font-bold text-white mb-1 font-heading" data-id="element-612">
                      {program.title}
                    </h3>
                    {'subtitle' in program && program.subtitle && <p className="text-gold text-xs uppercase tracking-[0.2em] font-bold mb-3" data-id="element-613">
                        {program.subtitle}
                      </p>}
                    {!('subtitle' in program && program.subtitle) && <div className="mb-3" data-id="element-614" />}
                    <p className="text-muted text-lg mb-8 flex-1 leading-relaxed" data-id="element-615">
                      {program.desc}
                    </p>
                    <Button to={program.link} variant="secondary" className="w-full group/btn" data-id="element-616">
                      Learn More{' '}
                      <ArrowRightIcon className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" data-id="element-617" />
                    </Button>
                  </div>
                </div>
              </AnimatedSection>)}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
                                                                    SECTION 10 — FINAL CTA
                                                                ═══════════════════════════════════════════════════════════════ */}
      <section className="py-32 bg-dark border-t border-gold relative overflow-hidden" data-id="element-618">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(212,175,55,0.05)_0%,transparent_70%)]" data-id="element-619"></div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10" data-id="element-620">
          <AnimatedSection data-id="element-621">
            <span className="text-gold text-sm font-medium uppercase tracking-[0.3em] mb-6 block" data-id="element-622">
              LIMITED AVAILABILITY
            </span>
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 font-heading leading-tight" data-id="element-623">
              Your Transformation Starts With a Single Decision
            </h2>
            <p className="text-xl text-muted mb-12 max-w-2xl mx-auto leading-relaxed" data-id="element-624">
              We accept a limited number of clients each quarter to ensure every
              individual receives the focused attention they deserve. If you're
              ready to commit to real change, apply below.
            </p>
            <Button href="https://holistic-health.bioshift.net/calender-booking" size="lg" className="mb-10" data-id="element-625">
              Apply for Your Strategy Call
            </Button>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-8 mb-12" data-id="element-626">
              <div className="flex flex-col items-center" data-id="element-627">
                <ShieldCheckIcon className="w-5 h-5 text-gold/60 mb-2" data-id="element-628" />
                <span className="text-muted text-xs uppercase tracking-wider" data-id="element-629">
                  Private & Confidential
                </span>
              </div>
              <div className="flex flex-col items-center" data-id="element-630">
                <ClockIcon className="w-5 h-5 text-gold/60 mb-2" data-id="element-631" />
                <span className="text-muted text-xs uppercase tracking-wider" data-id="element-632">
                  Response Within 48 Hours
                </span>
              </div>
              <div className="flex flex-col items-center" data-id="element-633">
                <UsersIcon className="w-5 h-5 text-gold/60 mb-2" data-id="element-634" />
                <span className="text-muted text-xs uppercase tracking-wider" data-id="element-635">
                  Limited to 15 Clients/Quarter
                </span>
              </div>
            </div>

            <p className="text-sm text-muted uppercase tracking-widest border-t border-border pt-8 inline-block" data-id="element-636">
              Dallas, Texas | Nationwide Online Coaching
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
                                                                    FLOATING CTA BAR
                                                                ═══════════════════════════════════════════════════════════════ */}
      <AnimatePresence data-id="element-637">
        {showFloatingBar && <motion.div initial={{
        y: 100,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 100,
        opacity: 0
      }} transition={{
        type: 'spring',
        stiffness: 260,
        damping: 20
      }} className="fixed bottom-0 left-0 w-full bg-dark/95 backdrop-blur-xl border-t border-gold/30 py-4 px-6 z-40 shadow-[0_-10px_30px_rgba(0,0,0,0.5)]" data-id="element-638">
            <div className="max-w-7xl mx-auto flex items-center justify-center md:justify-between" data-id="element-639">
              <p className="text-white text-sm font-medium hidden md:block" data-id="element-640">
                Ready to transform your health?
              </p>
              <Button href="https://holistic-health.bioshift.net/calender-booking" size="sm" className="w-full md:w-auto shadow-[0_0_15px_rgba(212,175,55,0.2)]" data-id="element-641">
                Apply Now — Limited Spots
              </Button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </main>;
}